/**
 * Admin Login Page
 * Página de login do Admin Console
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Lock, Mail, Zap, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { useAdminAuth } from '../hooks/useAdminAuth'

export default function Login() {
  const navigate = useNavigate()
  const { login, isLoading } = useAdminAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    
    try {
      await login(email, password)
      navigate('/dashboard')
    } catch (err) {
      setError('Credenciais inválidas. Tente novamente.')
    }
  }
  
  return (
    <div className="min-h-screen bg-ws-bg-primary flex items-center justify-center p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-br from-violet-600/20 to-transparent rounded-full blur-3xl" />
      
      {/* Login Card */}
      <div className="relative w-full max-w-md">
        <div className="absolute -inset-1 bg-gradient-to-br from-violet-500/20 to-purple-600/20 rounded-3xl blur-xl" />
        
        <div className="relative bg-ws-bg-secondary border border-slate-800 rounded-2xl p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', boxShadow: '0 0 40px rgba(139, 92, 246, 0.3)' }}>
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1">
              Work<span className="font-light italic text-violet-400">Stream</span>
            </h1>
            <p className="text-slate-500 text-sm font-mono">ADMIN CONSOLE</p>
          </div>
          
          {/* Error Alert */}
          {error && (
            <div className="mb-6 p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
              <p className="text-sm text-rose-400">{error}</p>
            </div>
          )}
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@workstream.com"
                  className="admin-input pl-12"
                  required
                />
              </div>
            </div>
            
            {/* Password */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Senha</label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="admin-input pl-12 pr-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-admin w-full py-3 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Entrando...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  Acessar Console
                </>
              )}
            </button>
          </form>
          
          {/* Footer */}
          <p className="mt-8 text-center text-xs text-slate-500">
            Acesso restrito a administradores WorkStream
          </p>
        </div>
      </div>
    </div>
  )
}

